import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { KIMBARK_KITCHEN_TAKEOFF } from '../data/kimbarkKitchenTakeoff';
import { KIMBARK_STAIRCASE_TAKEOFF } from '../data/kimbarkStaircaseTakeoff';
import { KIMBARK_FIREPLACE_TAKEOFF } from '../data/kimbarkFireplaceTakeoff';
import { KIMBARK_CABINET_TAKEOFF } from '../data/kimbarkCabinetTakeoff';
import type { ObbTakeoffData } from '../data/obbTakeoff';

export type ComparisonArea = 'kitchen' | 'staircase' | 'fireplace' | 'cabinet';

const SPLAT_QUATERNION = new THREE.Quaternion().setFromEuler(new THREE.Euler(-Math.PI / 2, 0, 0));

const EDGE_PAIRS = [
  [0,1],[1,3],[3,2],[2,0],
  [4,5],[5,7],[7,6],[6,4],
  [0,4],[1,5],[2,6],[3,7],
] as const;

function rawToWorld(raw: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(raw[0], raw[2], -raw[1]);
}

function topCenter(pts: [number, number, number][]): THREE.Vector3 {
  return rawToWorld([
    (pts[4][0] + pts[5][0] + pts[6][0] + pts[7][0]) / 4,
    (pts[4][1] + pts[5][1] + pts[6][1] + pts[7][1]) / 4,
    (pts[4][2] + pts[5][2] + pts[6][2] + pts[7][2]) / 4,
  ]);
}

function getObbAxes(pts: [number, number, number][]) {
  const rawCx = pts.reduce((s, p) => s + p[0], 0) / 8;
  const rawCy = pts.reduce((s, p) => s + p[1], 0) / 8;
  const rawCz = pts.reduce((s, p) => s + p[2], 0) / 8;
  const center = new THREE.Vector3(rawCx, rawCz, -rawCy);
  const p0 = new THREE.Vector3(...pts[0]);
  const p1 = new THREE.Vector3(...pts[1]);
  const p2 = new THREE.Vector3(...pts[2]);
  const p4 = new THREE.Vector3(...pts[4]);
  const e1 = new THREE.Vector3().subVectors(p1, p0);
  const e2 = new THREE.Vector3().subVectors(p2, p0);
  const e3 = new THREE.Vector3().subVectors(p4, p0);
  const halfLen1 = e1.length() / 2;
  const halfLen2 = e2.length() / 2;
  const halfLen3 = e3.length() / 2;
  const axisX = e1.normalize().applyQuaternion(SPLAT_QUATERNION);
  const dir2 = e2.normalize().applyQuaternion(SPLAT_QUATERNION);
  const dir3 = e3.normalize().applyQuaternion(SPLAT_QUATERNION);
  const [axisY, halfLenV, axisZ, halfLenZ] = Math.abs(dir3.y) >= Math.abs(dir2.y)
    ? [dir3, halfLen3, dir2, halfLen2]
    : [dir2, halfLen2, dir3, halfLen3];
  return { center, axisX, axisY, halfLen1, halfLenV, axisZ, halfLenZ };
}


// Zigzag polyline matching the shader's triangular-wave split boundary — kitchen only.
// The shader splits at lc_x = -zigzag(lc_y) * 0.35 * lc_h.x where zigzag is a
// periodic triangle wave with 5 teeth across the OBB height.
function buildZigzagLine(pts: [number, number, number][], colorHex: string, flipX = false): THREE.Group {
  const { center, axisX, axisY, halfLen1, halfLenV } = getObbAxes(pts);
  const hex = parseInt(colorHex.replace('#', ''), 16);
  const spread = halfLen1 * 0.012;
  const sign = flipX ? -1 : 1;
  const N = 80;

  const buildPositions = (xOffset: number): number[] => {
    const pos: number[] = [];
    for (let i = 0; i <= N; i++) {
      const t = i / N;                                 // 0=bottom → 1=top
      const tooth = (t * 5.0) % 1.0;                  // fract(norm_y2 * 5)
      const zigzag = (tooth < 0.5 ? tooth : 1.0 - tooth) * 2.0 - 0.5;  // -0.5 to 0.5
      const lc_x = sign * (-zigzag * 0.35 * halfLen1) + xOffset;
      const lc_y = t * 2.0 * halfLenV - halfLenV;
      const pt = center.clone().addScaledVector(axisX, lc_x).addScaledVector(axisY, lc_y);
      pos.push(pt.x, pt.y, pt.z);
    }
    return pos;
  };

  const makeLine = (offsetT: number, color: number, opacity: number) => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(buildPositions(offsetT * spread), 3));
    const mat = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true, opacity });
    const line = new THREE.Line(geo, mat);
    line.renderOrder = 12;
    return line;
  };

  const group = new THREE.Group();
  group.add(makeLine(-2.5, hex, 0.25));
  group.add(makeLine(-1,   hex, 0.65));
  group.add(makeLine(0,    0xffffff, 1.0));
  group.add(makeLine(1,    hex, 0.65));
  group.add(makeLine(2.5,  hex, 0.25));
  return group;
}

// Horizontal zigzag polyline matching the shader's triangular-wave horizontal split.
// Traces along axisZ (the longer horizontal axis); Y position follows zigzag(lc_z) * 0.35 * halfLenV.
function buildHorizontalLine(pts: [number, number, number][], colorHex: string): THREE.Group {
  const { center, axisY, halfLenV, axisZ, halfLenZ } = getObbAxes(pts);
  const hex = parseInt(colorHex.replace('#', ''), 16);
  const spread = halfLenV * 0.012;
  const N = 80;

  const buildPositions = (yOffset: number): number[] => {
    const pos: number[] = [];
    for (let i = 0; i <= N; i++) {
      const t = i / N;                                 // 0 → 1 along axisZ
      const tooth = (t * 5.0) % 1.0;
      const zigzag = (tooth < 0.5 ? tooth : 1.0 - tooth) * 2.0 - 0.5;
      const lc_z = t * 2.0 * halfLenZ - halfLenZ;
      const lc_y = -(zigzag * 0.35 * halfLenV) + yOffset;
      const pt = center.clone().addScaledVector(axisZ, lc_z).addScaledVector(axisY, lc_y);
      pos.push(pt.x, pt.y, pt.z);
    }
    return pos;
  };

  const makeLine = (offsetT: number, color: number, opacity: number) => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(buildPositions(offsetT * spread), 3));
    const mat = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true, opacity });
    const line = new THREE.Line(geo, mat);
    line.renderOrder = 12;
    return line;
  };

  const group = new THREE.Group();
  group.add(makeLine(-2.5, hex, 0.25));
  group.add(makeLine(-1,   hex, 0.65));
  group.add(makeLine(0,    0xffffff, 1.0));
  group.add(makeLine(1,    hex, 0.65));
  group.add(makeLine(2.5,  hex, 0.25));
  return group;
}

// Builds a split OBB where each half of the zigzag gets its own edge color and fill.
// splitMode undefined → vertical split (left/right along short axis P0↔P1)
// splitMode 'horizontal' → top/bottom split along height axis P0↔P4
function buildObbGroup(
  pts: [number, number, number][],
  color1Hex: string,
  color2Hex: string,
  splitMode?: 'horizontal',
  flipX = false,
): THREE.Group {
  const c1raw = parseInt(color1Hex.replace('#', ''), 16);
  const c2raw = parseInt(color2Hex.replace('#', ''), 16);
  const [c1, c2] = flipX ? [c2raw, c1raw] : [c1raw, c2raw];

  const mid = (a: [number,number,number], b: [number,number,number]): [number,number,number] =>
    [(a[0]+b[0])/2, (a[1]+b[1])/2, (a[2]+b[2])/2];

  const group = new THREE.Group();
  group.quaternion.copy(SPLAT_QUATERNION);

  const addLines = (segs: number[], color: number) => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(segs, 3));
    const mat = new THREE.LineBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.88 });
    const ls = new THREE.LineSegments(geo, mat);
    ls.renderOrder = 10;
    group.add(ls);
  };

  const addFill = (verts: number[], color: number, opacity = 0.13) => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity, side: THREE.DoubleSide, depthTest: false });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.renderOrder = 9;
    group.add(mesh);
  };

  const v = (i: number) => pts[i];

  if (!splitMode) {
    // ── Vertical split: color1 = P0/P2/P4/P6 side, color2 = P1/P3/P5/P7 side ──
    const M01 = mid(v(0), v(1));
    const M23 = mid(v(2), v(3));
    const M45 = mid(v(4), v(5));
    const M67 = mid(v(6), v(7));

    // color1 edges: long sides + verticals on the P0/P2/P4/P6 half + half-short edges
    addLines([
      ...v(0), ...v(2),   ...v(4), ...v(6),   // long edges
      ...v(0), ...v(4),   ...v(2), ...v(6),   // verticals
      ...v(0), ...M01,    ...v(2), ...M23,    // half-short bottom
      ...v(4), ...M45,    ...v(6), ...M67,    // half-short top
      ...M01, ...M45,     ...M23, ...M67,     // centre divider verticals
      ...M01, ...M23,     ...M45, ...M67,     // centre divider horizontals
    ], c1);

    // color2 edges: the P1/P3/P5/P7 half
    addLines([
      ...v(1), ...v(3),   ...v(5), ...v(7),   // long edges
      ...v(1), ...v(5),   ...v(3), ...v(7),   // verticals
      ...M01, ...v(1),    ...M23, ...v(3),    // half-short bottom
      ...M45, ...v(5),    ...M67, ...v(7),    // half-short top
    ], c2);

    // fills
    addFill([...v(4), ...M45, ...M67, ...v(4), ...M67, ...v(6)], c1);            // top-left face
    addFill([...M45, ...v(5), ...v(7), ...M45, ...v(7), ...M67], c2);            // top-right face
    addFill([...v(0), ...v(4), ...v(6), ...v(0), ...v(6), ...v(2)], c1, 0.08);  // back-left face
    addFill([...v(1), ...v(5), ...v(7), ...v(1), ...v(7), ...v(3)], c2, 0.08);  // front-right face
  } else {
    // ── Horizontal split: color1 = top half (P4-P7), color2 = bottom half (P0-P3) ──
    const M04 = mid(v(0), v(4));
    const M15 = mid(v(1), v(5));
    const M26 = mid(v(2), v(6));
    const M37 = mid(v(3), v(7));

    // color1 edges: top half
    addLines([
      ...v(4), ...v(5),   ...v(5), ...v(7),   ...v(7), ...v(6),   ...v(6), ...v(4),  // top face
      ...M04, ...v(4),    ...M15, ...v(5),    ...M26, ...v(6),    ...M37, ...v(7),    // upper verticals
      ...M04, ...M15,     ...M15, ...M37,     ...M37, ...M26,     ...M26, ...M04,     // mid face
    ], c1);

    // color2 edges: bottom half
    addLines([
      ...v(0), ...v(1),   ...v(1), ...v(3),   ...v(3), ...v(2),   ...v(2), ...v(0),  // bottom face
      ...v(0), ...M04,    ...v(1), ...M15,    ...v(2), ...M26,    ...v(3), ...M37,    // lower verticals
    ], c2);

    // fills
    addFill([...v(4), ...v(5), ...v(7), ...v(4), ...v(7), ...v(6)], c1);             // top face
    addFill([...v(0), ...v(1), ...v(3), ...v(0), ...v(3), ...v(2)], c2);             // bottom face
    addFill([...v(4), ...M04, ...M26, ...v(4), ...M26, ...v(6)], c1, 0.08);          // side-top face
    addFill([...M04, ...v(0), ...v(2), ...M04, ...v(2), ...M26], c2, 0.08);          // side-bottom face
  }

  return group;
}

interface AreaConfig {
  data: ObbTakeoffData;
  name: string;
  change: string;
  anchor: THREE.Vector3;
  color2: string;
  flipX?: boolean;
  splitMode?: 'horizontal';
}

export const COMPARISON_AREAS: Record<ComparisonArea, AreaConfig> = {
  kitchen: {
    data: KIMBARK_KITCHEN_TAKEOFF,
    name: 'Kitchen',
    change: "New island installed · 8' 5\" × 4' 2\" × 38\"",
    anchor: topCenter(KIMBARK_KITCHEN_TAKEOFF.obbPoints),
    color2: '#a78bfa',
  },
  staircase: {
    data: KIMBARK_STAIRCASE_TAKEOFF,
    name: 'Staircase',
    change: "Staircase framing complete · 9' 6\" × 4' 6\" × 8' 5\"",
    anchor: topCenter(KIMBARK_STAIRCASE_TAKEOFF.obbPoints),
    color2: '#f472b6',
    splitMode: 'horizontal',
  },
  fireplace: {
    data: KIMBARK_FIREPLACE_TAKEOFF,
    name: 'Fireplace',
    change: "Fireplace surround installed · 5' 7\" W × 4' 4\" H",
    anchor: topCenter(KIMBARK_FIREPLACE_TAKEOFF.obbPoints),
    color2: '#22d3ee',
    flipX: true,
    splitMode: 'horizontal',
  },
  cabinet: {
    data: KIMBARK_CABINET_TAKEOFF,
    name: 'Kitchen Cabinets',
    change: "Kitchen cabinets installed · 11' 9\" × 2' 5\" × 8' 6\"",
    anchor: topCenter(KIMBARK_CABINET_TAKEOFF.obbPoints),
    color2: '#fbbf24',
    splitMode: 'horizontal',
  },
};

interface Props {
  viewerRef: React.MutableRefObject<{
    threeScene?: THREE.Scene;
    camera?: THREE.PerspectiveCamera;
    renderer?: THREE.WebGLRenderer;
  }>;
  area: ComparisonArea;
}

export function ProgressComparisonOverlay({ viewerRef, area }: Props) {
  const rafRef = useRef<number | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scene = viewerRef.current.threeScene;
    if (!scene) return;

    const areaConfig = COMPARISON_AREAS[area];
    const group = buildObbGroup(areaConfig.data.obbPoints, areaConfig.data.color, areaConfig.color2, areaConfig.splitMode, areaConfig.flipX);
    const splitLine = areaConfig.splitMode === 'horizontal'
      ? buildHorizontalLine(areaConfig.data.obbPoints, areaConfig.data.color)
      : buildZigzagLine(areaConfig.data.obbPoints, areaConfig.data.color, areaConfig.flipX);
    scene.add(group);
    scene.add(splitLine);

    const proj = new THREE.Vector3();
    const tick = () => {
      rafRef.current = requestAnimationFrame(tick);
      const camera = viewerRef.current.camera;
      const canvas = viewerRef.current.renderer?.domElement;
      if (!camera || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const el = labelRef.current;
      if (!el) return;
      proj.copy(areaConfig.anchor).project(camera);
      if (proj.z > 1) { el.style.display = 'none'; return; }
      const x = (proj.x + 1) / 2 * rect.width + rect.left;
      const y = (1 - proj.y) / 2 * rect.height + rect.top;
      el.style.display = 'block';
      el.style.transform = `translate(-50%,-100%) translate(${x}px,${y}px)`;
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      scene.remove(group);
      scene.remove(splitLine);
      group.traverse(child => {
        if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
      splitLine.traverse(child => {
        if (child instanceof THREE.Line) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
    };
  }, [viewerRef, area]);

  const areaConfig = COMPARISON_AREAS[area];

  return (
    <div
      ref={labelRef}
      style={{
        position: 'fixed', top: 0, left: 0,
        pointerEvents: 'none', zIndex: 1020,
        display: 'none',
        background: 'rgba(10,12,16,0.88)',
        border: `1px solid ${areaConfig.data.color}88`,
        borderRadius: 8,
        padding: '5px 10px',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        textAlign: 'center',
        whiteSpace: 'nowrap',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: areaConfig.data.color, marginBottom: 2 }}>
        {areaConfig.name}
      </div>
      <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.55)' }}>
        {areaConfig.change}
      </div>
    </div>
  );
}

export default ProgressComparisonOverlay;
